import { useMemo } from 'react'

import i18n from 'modules/core/lib/i18n'

import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import type Leave from 'modules/timeoff/store/models/Leave'

interface Copies {
  title: string
  description: string
}

const otherCopies = (leave: Leave): Copies => {
  if (leave.isPending()) {
    return {
      title: i18n.t('time_off.admin.review.title'),
      description: i18n.tCurrent('time_off.admin.review.subtitle', false),
    }
  }

  if (leave.isRejected()) {
    return {
      title: i18n.t('time_off.admin.edit_rejected.title'),
      description: i18n.t('time_off.admin.rejected'),
    }
  }

  return {
    title: i18n.t('time_off.admin.edit_approved.title'),
    description: i18n.tCurrent('time_off.admin.edit_approved.subtitle', false),
  }
}

const buildPastBasicCopies = (form: LeaveForm): Copies => {
  if (form.isSickType()) {
    return {
      title: i18n.t('time_off.employee.review_approved_past_time_off.title'),
      description: i18n.t('time_off.employee.review_approved_past_time_off.subtitle.sick'),
    }
  }

  return {
    title: i18n.t('time_off.employee.review_approved_past_time_off.title'),
    description: i18n.t('time_off.employee.review_approved_past_time_off.subtitle.other'),
  }
}

const employeeCopies = (
  leave: Leave,
  form: LeaveForm,
  timeoffManagerFullName: string | null
): Copies => {
  if (leave.isRejected()) {
    return {
      title: i18n.t('time_off.admin.edit_rejected.title'),
      description: i18n.t('time_off.employee.rejected'),
    }
  }

  if (leave.isPending()) {
    return {
      title: i18n.t('time_off.employee.edit_pending.title'),
      description: i18n.t('time_off.employee.edit_pending.subtitle'),
    }
  }

  if (form.hasLeaveStarted()) return buildPastBasicCopies(form)

  if (form.isSickType()) {
    return {
      title: i18n.t('time_off.employee.edit_approved.title'),
      description: i18n.t('time_off.employee.edit_approved.subtitle.sick'),
    }
  }

  if (timeoffManagerFullName) {
    return {
      title: i18n.t('time_off.employee.edit_approved.title'),
      description: i18n.t('time_off.employee.edit_approved.subtitle.not_autoapproved', {
        timeoffManager: timeoffManagerFullName,
      }),
    }
  }

  return {
    title: i18n.t('time_off.employee.edit_approved.title'),
    description: i18n.t('time_off.employee.edit_approved.subtitle.autoapproved'),
  }
}

interface Props {
  leave: Leave
  form: LeaveForm
  canApprove: boolean
  isCurrentEmployee: boolean
  timeoffManagerFullName: string | null
}

const useModalCopies = ({
  form,
  leave,
  isCurrentEmployee,
  timeoffManagerFullName,
}: Props): Copies => {
  const { title, description } = useMemo<Copies>(() => {
    if (isCurrentEmployee) {
      return employeeCopies(leave, form, timeoffManagerFullName)
    }

    return otherCopies(leave)
  }, [leave, form, isCurrentEmployee, timeoffManagerFullName])

  return {
    title,
    description,
  }
}

export default useModalCopies
